/**
 * Pin-to-dashboard confirmation sheet (S20) — deliberately NOT the Add Tile
 * dialog: dashboard pick (+ new, free-cap aware), title, the autopilot's
 * detected tile type, Done. Mounted ONCE at the app root (PinConfirmSheet in
 * App.tsx); surfaces open it via `requestPinToDashboard`.
 */
import { useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from '@/components/ui/toaster';
import { useAnalytics } from '@/core/analytics/useAnalytics';
import { getEngineForConnection } from '@/features/connections/utils/connectionLookup';
import { FREE_DASHBOARD_LIMIT, type DashboardEngineId } from '../types/dashboardTypes';
import { useDashboardStore } from '../store/dashboardStore';
import { openDashboardTab } from '../utils/openDashboardTab';
import {
  buildPinnedTile,
  defaultPinTitle,
  suggestForPin,
  usePinToDashboardStore,
  type PinRequest,
} from '../services/pinToDashboard';

const KNOWN_ENGINES = new Set(['mssql', 'pgsql', 'mysql', 'oracle', 'sqlite', 'kusto']);
const NEW_DASHBOARD = '__new__';

export function PinConfirmSheet() {
  const request = usePinToDashboardStore((s) => s.request);
  const nonce = usePinToDashboardStore((s) => s.nonce);
  const close = usePinToDashboardStore((s) => s.close);
  const loaded = useDashboardStore((s) => s.loaded);
  const load = useDashboardStore((s) => s.load);

  useEffect(() => {
    if (request && !loaded) void load();
  }, [request, loaded, load]);

  // Wait for hydration — the target Select's initial value is computed at
  // mount, so mounting before load() resolves would default a first-of-session
  // pin to "+ New dashboard" even when dashboards exist.
  if (!request || !loaded) return null;
  return <PinSheetInner key={nonce} request={request} onClose={close} />;
}

function PinSheetInner({ request, onClose }: { request: PinRequest; onClose: () => void }) {
  const { track } = useAnalytics();
  const dashboards = useDashboardStore((s) => s.dashboards);
  const recentFirst = useMemo(
    () => [...dashboards].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt)),
    [dashboards],
  );

  // Derived from what the surface pinned (hint → chart title → the query's
  // relation), never the generic "Pinned query" (S24 review).
  const fallbackTitle = useMemo(() => defaultPinTitle(request), [request]);
  const [title, setTitle] = useState(fallbackTitle);
  const [target, setTarget] = useState<string>(recentFirst[0]?.id ?? NEW_DASHBOARD);
  const [newName, setNewName] = useState('');
  const [capError, setCapError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const suggestion = useMemo(() => suggestForPin(request), [request]);

  const pin = async () => {
    setBusy(true);
    setCapError(null);
    try {
      const store = useDashboardStore.getState();
      let targetId = target;
      if (target === NEW_DASHBOARD) {
        const created = await store.create(newName.trim() || title.trim() || 'New dashboard');
        if (!created.ok) {
          if (created.error === 'free-limit') {
            // Keep the reason local to the sheet — not the dashboard-tab banner.
            store.clearFreeLimit();
            setCapError(
              created.detail ??
                `Free plan is limited to ${FREE_DASHBOARD_LIMIT} dashboards. Upgrade to Pro for unlimited.`,
            );
          } else {
            toast.error({ title: 'Could not create dashboard', description: created.detail });
          }
          return;
        }
        targetId = created.value.id;
      }

      const engineRaw = getEngineForConnection(request.connectionId);
      const engine = KNOWN_ENGINES.has(engineRaw) ? (engineRaw as DashboardEngineId) : undefined;
      const existing = store.dashboards.find((d) => d.id === targetId)?.tiles ?? [];
      const tile = buildPinnedTile({
        request,
        title: title.trim() || fallbackTitle,
        suggestion,
        engine,
        existingTiles: existing,
      });
      const added = await store.addTile(targetId, tile);
      if (!added.ok) {
        toast.error({ title: 'Could not pin to dashboard', description: added.detail });
        return;
      }
      track('feature:dashboard_pin', { from: request.from, engine: engineRaw });
      toast.success({
        title: `Pinned to “${added.value.name}”`,
        action: { label: 'Open', onClick: () => openDashboardTab(targetId) },
      });
      onClose();
    } finally {
      setBusy(false);
    }
  };

  const needsNewName = target === NEW_DASHBOARD;

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-sm" data-testid="pin-sheet">
        <DialogHeader>
          <DialogTitle>Pin to dashboard</DialogTitle>
          <DialogDescription>
            Lands as a {suggestion.tileType} tile — {suggestion.reason.toLowerCase()}. Refine it
            later with Configure…
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="pin-title" className="text-xs">
              Title
            </Label>
            <Input
              id="pin-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="h-8"
              data-testid="pin-title-input"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label className="text-xs">Dashboard</Label>
            <Select value={target} onValueChange={setTarget}>
              <SelectTrigger className="h-8" data-testid="pin-dashboard-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {recentFirst.map((d) => (
                  <SelectItem key={d.id} value={d.id} data-testid={`pin-dashboard-option-${d.id}`}>
                    {d.name}
                  </SelectItem>
                ))}
                <SelectItem value={NEW_DASHBOARD} data-testid="pin-dashboard-option-new">
                  + New dashboard
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
          {needsNewName && (
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="pin-new-name" className="text-xs">
                New dashboard name
              </Label>
              <Input
                id="pin-new-name"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder={title.trim() || 'New dashboard'}
                className="h-8"
                data-testid="pin-new-dashboard-name"
              />
            </div>
          )}
          {capError && (
            <p className="text-xs text-destructive" data-testid="pin-free-limit-error">
              {capError}
            </p>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onClose} data-testid="pin-cancel">
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={() => void pin()}
            disabled={busy}
            className="disabled:bg-muted disabled:text-muted-foreground"
            data-testid="pin-confirm"
          >
            Pin
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
